import { useEffect, useState } from 'react';

export interface OnlineStatus {
  isOnline: boolean;
  /** True once the connection has dropped and come back. */
  wasOffline: boolean;
  /** Clears `wasOffline` after the caller has acted on the reconnect. */
  acknowledgeReconnect: () => void;
}

/**
 * Tracks connectivity via the browser's online/offline events.
 *
 * `navigator.onLine` only reports whether the OS has a network interface up —
 * it can read true while requests still fail. It is a reliable signal for the
 * *offline* direction, which is what autosave needs: when it says offline, the
 * save button must not attempt a request. A false "online" is harmless here
 * because the save simply fails and the draft stays put.
 */
export function useOnlineStatus(): OnlineStatus {
  const [isOnline, setIsOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));
  // Scoped to this hook instance: a reconnect is only interesting to a form
  // that was mounted while the connection dropped.
  const [wasOffline, setWasOffline] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(prevOnline => {
        // Only flag a reconnect when we actually observed the offline
        // transition, so a fresh mount on a healthy connection stays quiet.
        if (!prevOnline) setWasOffline(true);
        return true;
      });
    };

    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return {
    isOnline,
    wasOffline,
    acknowledgeReconnect: () => setWasOffline(false),
  };
}

export default useOnlineStatus;
